import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Layout } from './components/Layout';
import { AccountPage } from './features/account/pages/AccountPage';
import { ManageSitesPage } from './features/admin-sites/pages/ManageSitesPage';
import { AdminUsersPage } from './features/admin/pages/AdminUsersPage';
import { CompanyProfilePage } from './features/company/pages/CompanyProfilePage';
import { DocumentViewPage } from './features/documents/pages/DocumentViewPage';
import { DocumentsPage } from './features/documents/pages/DocumentsPage';
import { ContextDocumentPage } from './features/documents/pages/context/ContextDocumentPage';
import { ModulesPage } from './features/modules/pages/ModulesPage';
import { CartographyPage } from './features/process-cartography/pages/CartographyPage';
import { GroupsProcessesPage } from './features/process-cartography/pages/GroupsProcessesPage';
import { ItemCategoriesPage } from './features/process-cartography/pages/ItemCategoriesPage';
import { CertificationJourneyPage } from './features/progression/pages/CertificationJourneyPage';
import { PendingValidationsPage } from './features/publication/pages/PendingValidationsPage';
import { PublishedDocumentsPage } from './features/publication/pages/PublishedDocumentsPage';
import { RejectedSubmissionsPage } from './features/publication/pages/RejectedSubmissionsPage';

/** Paramètres d’URL pour `/documents/$id` (alignés sur `documentViewRoute.validateSearch`). */
export const documentViewSearch = {
  closed: { openDocumentRights: false, view: undefined as 'published' | undefined },
  openRights: { openDocumentRights: true, view: undefined as 'published' | undefined },
  published: { openDocumentRights: false, view: 'published' as const },
} as const;

const rootRoute = createRootRoute({
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: CertificationJourneyPage,
});

const cartographieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cartographie',
  component: CartographyPage,
});

const parametresGroupesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parametres/groupes-et-processus',
  component: GroupsProcessesPage,
});

const parametresStructureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parametres/structure-des-processus',
  component: ItemCategoriesPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  validateSearch: (raw: Record<string, unknown>) => ({
    tab: raw.tab === 'groups' ? ('groups' as const) : ('users' as const),
  }),
  component: function AdminUsersRouteComponent() {
    const { tab } = adminUsersRoute.useSearch();
    return <AdminUsersPage initialTab={tab} />;
  },
});

const adminCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/company',
  component: CompanyProfilePage,
});

const adminSitesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/sites',
  component: ManageSitesPage,
});

const modulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/modules',
  component: ModulesPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  component: DocumentsPage,
});

const documentViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/$id',
  validateSearch: (raw: Record<string, unknown>) => ({
    openDocumentRights:
      raw.openDocumentRights === true ||
      raw.openDocumentRights === 'true' ||
      raw.openDocumentRights === '1',
    view: raw.view === 'published' ? ('published' as const) : undefined,
  }),
  component: function DocumentViewRouteComponent() {
    const { id } = documentViewRoute.useParams();
    const { openDocumentRights, view } = documentViewRoute.useSearch();
    return (
      <DocumentViewPage
        id={id}
        openDocumentRights={openDocumentRights}
        viewPublished={view === 'published'}
      />
    );
  },
});

const publicationPendingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/publication/a-valider',
  component: PendingValidationsPage,
});

const publicationPublishedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/publication/publie',
  component: PublishedDocumentsPage,
});

const publicationRejectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/publication/refus',
  component: RejectedSubmissionsPage,
});

const contexteSwotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contexte/swot',
  component: () => <ContextDocumentPage extension="swot" />,
});

const contexteStakeholdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contexte/parties-prenantes',
  validateSearch: (raw: Record<string, unknown>) => ({
    openDocumentRights:
      raw.openDocumentRights === true ||
      raw.openDocumentRights === 'true' ||
      raw.openDocumentRights === '1',
  }),
  component: function ContexteStakeholdersRouteComponent() {
    const { openDocumentRights } = contexteStakeholdersRoute.useSearch();
    return (
      <ContextDocumentPage extension="needsWait" forwardOpenDocumentRights={openDocumentRights} />
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  cartographieRoute,
  parametresGroupesRoute,
  parametresStructureRoute,
  accountRoute,
  adminUsersRoute,
  adminCompanyRoute,
  adminSitesRoute,
  modulesRoute,
  documentsRoute,
  documentViewRoute,
  publicationPendingRoute,
  publicationRejectedRoute,
  publicationPublishedRoute,
  contexteSwotRoute,
  contexteStakeholdersRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
